import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import * as api from '../../../requests'
import { setErrorToast, setSuccessToast } from '../../../actions'
import { SUBROUTES } from '../../../App'

const defaultDto = {
  MiscellaneousFieldId: null,
  MiscellaneousModuleId: null,
  FieldName: '',
  FieldDisplayName: '',
  DataType: '',
  IsStaffSearchable: false,
  ShowInStaffSearchGrid: false,
  IsPublicSearchable: false,
  IsMainRecordNameField: false,
  DisplayOnPublicSlideOut: false,
  ShowInPublicSearchGrid: false,
  IsMyRecordsFilter: false,
  DisplayOnRecordDetail: false,
  LookupTypeValuesJson: '',
  IsServerDropdownOption: false,
  IsEncrypted: false,
  TrackChangesInHistory: false,
  ShowInPublicHistory: false,
  ConfigurableAsFilingRequirement: false,
  ConfigurableAsFilingResult: false,
  DisplayInWebProcessModal: false,
  IsPaperProcessModalSearchable: false,
  IsDeleted: false,
  LastModifiedByUserId: null,
  LastModifiedByUser: '',
  LastModifiedByDate: null,
}

const FieldDefinitionForm = ({
  moduleId,
  fieldDefinition: fieldDefinitionProp,
  onSave,
  onCancel,
  dataTypeOptions,
  readOnly = false,
}) => {
  const dispatch = useDispatch()
  const [fieldDefinition, setFieldDefinition] = useState({
    ...defaultDto,
    ...(fieldDefinitionProp || {}),
    MiscellaneousModuleId: moduleId,
  })
  const [isSaving, setIsSaving] = useState(false)
  const isFirstRender = useRef(true)

  const allModules = (SUBROUTES || [])
    .filter((r) => r.IsMiscRecordsType)
    .flatMap((r) => r.MiscRecordsModules || [])

  const selectedModule = allModules.find((m) => m.MiscellaneousModuleId === +moduleId) || {}

  const moduleDisplayName = selectedModule.DisplayName || moduleId

  const moduleHasPublicSearch = selectedModule.HasPublicSearch || false

  const { DataType: dataType } = fieldDefinition

  const selectedTypeLabel =
    (dataTypeOptions.find((opt) => +opt.value === +dataType) || {}).label || ''

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const label = (dataTypeOptions.find((opt) => opt.value === dataType) || {}).label || ''
    const updates = {}
    if (label === 'Address' || label === 'PhoneNumber') {
      updates.IsStaffSearchable = false
      updates.IsPublicSearchable = false
      updates.IsMyRecordsFilter = false
    }
    if (label !== 'LookupType') {
      updates.LookupTypeValuesJson = ''
      updates.IsServerDropdownOption = false
    }
    if (!['String', 'Name'].includes(label)) {
      updates.IsMainRecordNameField = false
    }
    if (!['String', 'Address', 'Name', 'PhoneNumber'].includes(label)) {
      updates.IsEncrypted = false
    }
    if (['PhoneNumber', 'String', 'Address', 'Name', 'Int'].includes(label)) {
      updates.ConfigurableAsFilingRequirement = false
      updates.ConfigurableAsFilingResult = false
    }
    setFieldDefinition((prev) => ({ ...prev, ...updates }))
  }, [dataType])

  const handleSave = () => {
    setIsSaving(true)
    const method = fieldDefinition.MiscellaneousFieldId ? 'PUT' : 'POST'
    api.saveMiscRecordsFieldDefinition(
      method,
      {
        ...fieldDefinition,
        MiscellaneousModuleId: +moduleId,
        DataType: +fieldDefinition.DataType,
        LookupTypeValuesJson: fieldDefinition.LookupTypeValuesJson,
      },
      (res) => {
        setIsSaving(false)
        setFieldDefinition((prev) => ({ ...prev, ...res }))
        dispatch(setSuccessToast('Field definition saved successfully.'))
        onSave(res)
      },
      (err) => {
        setIsSaving(false)
        dispatch(
          setErrorToast(
            err && err.DisplayToUser && err.Message
              ? err.Message
              : 'Failed to save field definition.',
          ),
        )
      },
    )
  }

  const setField = (field) => (val) => setFieldDefinition((prev) => ({ ...prev, [field]: val }))
  const setIsPublicSearchable = (val) =>
    setFieldDefinition((prev) => ({
      ...prev,
      IsPublicSearchable: val,
    }))
  const setIsMainRecordNameField = (val) =>
    setFieldDefinition((prev) => ({
      ...prev,
      IsMainRecordNameField: val,
      IsStaffSearchable: val,
      ShowInStaffSearchGrid: val,
      IsPublicSearchable: moduleHasPublicSearch ? val : false,
      DisplayOnPublicSlideOut: val,
      ShowInPublicSearchGrid: moduleHasPublicSearch ? val : false,
      IsMyRecordsFilter: val,
      DisplayOnRecordDetail: val,
      DisplayInWebProcessModal: val,
      IsPaperProcessModalSearchable: val,
    }))
  const setFieldText = (field) => (e) =>
    setFieldDefinition((prev) => ({ ...prev, [field]: e.target.value }))

  const isMainField = !!fieldDefinition.IsMainRecordNameField
  const isStringOrName = dataType !== '' && ['String', 'Name'].includes(selectedTypeLabel)
  const isAddressOrPhone = selectedTypeLabel === 'Address' || selectedTypeLabel === 'PhoneNumber'
  const isLookupType = selectedTypeLabel === 'LookupType'
  const isEncryptable = ['String', 'Address', 'Name', 'PhoneNumber'].includes(selectedTypeLabel)
  const isFilingConfigurable = !['PhoneNumber', 'String', 'Address', 'Name', 'Int'].includes(
    selectedTypeLabel,
  )
  const fieldNameRegexPattern = '^[a-zA-Z0-9]+$'
  const fieldNameValid = fieldDefinition.FieldName
    ? new RegExp(fieldNameRegexPattern).test(fieldDefinition.FieldName)
    : null

  return (
    <div className="field-definition-form">
      <div className="field-definition-form__module-name">
        <label>Miscellaneous Module: </label>
        <span>{moduleDisplayName}</span>
      </div>

      {fieldDefinition.MiscellaneousFieldId && (
        <div className="field-definition-form__metadata">
          <div>
            Field Definition Id: <strong>{fieldDefinition.MiscellaneousFieldId}</strong>
          </div>
          <div>
            Last Modified By: <strong>{fieldDefinition.LastModifiedByUser}</strong>
          </div>
          <div>
            Last Modified Date: <strong>{fieldDefinition.LastModifiedByDate}</strong>
          </div>
        </div>
      )}

      <TextInput
        label="Field Name"
        value={fieldDefinition.FieldName}
        onChange={setFieldText('FieldName')}
        valid={fieldNameValid === true ? undefined : fieldNameValid}
        errorMessage={
          fieldNameValid === false ? 'Field name must contain only letters and numbers' : undefined
        }
        disabled={readOnly}
      />
      <TextInput
        label="Field Display Name"
        value={fieldDefinition.FieldDisplayName}
        onChange={setFieldText('FieldDisplayName')}
        disabled={readOnly}
      />
      <SelectBox
        label="Data Type"
        value={fieldDefinition.DataType}
        onChange={setField('DataType')}
        options={dataTypeOptions}
        disabled={readOnly}
      />
      <Checkbox
        label="Is Main Record Name Field"
        checked={fieldDefinition.IsMainRecordNameField}
        onChange={setIsMainRecordNameField}
        disabled={!isStringOrName || readOnly}
        description={!isStringOrName ? 'Only applicable for String or Name data types' : undefined}
      />
      <Checkbox
        label="Is Staff Searchable"
        checked={fieldDefinition.IsStaffSearchable}
        onChange={setField('IsStaffSearchable')}
        disabled={isMainField || isAddressOrPhone || readOnly}
        description={
          isMainField
            ? 'Always enabled for the Is Main Record Name Field.'
            : isAddressOrPhone
              ? 'Not applicable for Address or PhoneNumber data types'
              : undefined
        }
      />
      <Checkbox
        label="Show In Staff Search Grid"
        checked={fieldDefinition.ShowInStaffSearchGrid}
        onChange={setField('ShowInStaffSearchGrid')}
        disabled={isMainField || readOnly}
        description={isMainField ? 'Always enabled for the Is Main Record Name Field.' : undefined}
      />
      <Checkbox
        label="Is Public Searchable"
        checked={moduleHasPublicSearch ? fieldDefinition.IsPublicSearchable : false}
        onChange={setIsPublicSearchable}
        disabled={!moduleHasPublicSearch || isMainField || isAddressOrPhone || readOnly}
        description={
          !moduleHasPublicSearch
            ? 'This module does not have public search enabled.'
            : isMainField
              ? 'Always enabled for the Is Main Record Name Field.'
              : isAddressOrPhone
                ? 'Not applicable for Address or PhoneNumber data types'
                : undefined
        }
      />
      <Checkbox
        label="Show In Public Search Grid"
        checked={moduleHasPublicSearch ? fieldDefinition.ShowInPublicSearchGrid : false}
        onChange={setField('ShowInPublicSearchGrid')}
        disabled={!moduleHasPublicSearch || isMainField || readOnly}
        description={
          !moduleHasPublicSearch
            ? 'This module does not have public search enabled.'
            : isMainField
              ? 'Always enabled for the Is Main Record Name Field.'
              : undefined
        }
      />
      <Checkbox
        label="Display on My Records/Public Search Slide-Out"
        checked={fieldDefinition.DisplayOnPublicSlideOut}
        onChange={setField('DisplayOnPublicSlideOut')}
        disabled={isMainField || readOnly}
        description={isMainField ? 'Always enabled for the Is Main Record Name Field.' : undefined}
      />
      <Checkbox
        label="Is My Records Filter"
        checked={fieldDefinition.IsMyRecordsFilter}
        onChange={setField('IsMyRecordsFilter')}
        disabled={isMainField || isAddressOrPhone || readOnly}
        description={
          isMainField
            ? 'Always enabled for the Is Main Record Name Field.'
            : isAddressOrPhone
              ? 'Not applicable for Address or PhoneNumber data types'
              : undefined
        }
      />
      <Checkbox
        label="Display On Record Detail"
        checked={fieldDefinition.DisplayOnRecordDetail}
        onChange={setField('DisplayOnRecordDetail')}
        disabled={isMainField || readOnly}
        description={isMainField ? 'Always enabled for the Is Main Record Name Field.' : undefined}
      />
      <Checkbox
        label="Display In Web Process Modal"
        checked={fieldDefinition.DisplayInWebProcessModal}
        onChange={setField('DisplayInWebProcessModal')}
        disabled={isMainField || readOnly}
        description={isMainField ? 'Always enabled for the Is Main Record Name Field.' : undefined}
      />
      <Checkbox
        label="Is Paper Process Modal Searchable"
        checked={fieldDefinition.IsPaperProcessModalSearchable}
        onChange={setField('IsPaperProcessModalSearchable')}
        disabled={isMainField || readOnly}
        description={isMainField ? 'Always enabled for the Is Main Record Name Field.' : undefined}
      />
      <div className="field-definition-form__textarea-field">
        <div className="field-definition-form__textarea-label">Lookup Type Values (JSON)</div>
        <textarea
          rows={isLookupType ? 6 : 1}
          value={fieldDefinition.LookupTypeValuesJson}
          onChange={setFieldText('LookupTypeValuesJson')}
          disabled={!isLookupType || readOnly}
        />
        <div className="field-definition-form__textarea-description">
          {isLookupType ? (
            <pre>{`Example:\n[\n  { "value": "example1", "label": "Example Label 1" },\n  { "value": "example2", "label": "Example Label 2" }\n]`}</pre>
          ) : (
            'Only applicable for LookupType data type'
          )}
        </div>
      </div>
      <Checkbox
        label="Is Server Dropdown Option"
        checked={fieldDefinition.IsServerDropdownOption}
        onChange={setField('IsServerDropdownOption')}
        disabled={!isLookupType || readOnly}
        description={!isLookupType ? 'Only applicable for LookupType data type' : undefined}
      />
      <Checkbox
        label="Is Encrypted"
        checked={fieldDefinition.IsEncrypted}
        onChange={setField('IsEncrypted')}
        disabled={!isEncryptable || readOnly}
        description={
          !isEncryptable
            ? 'Only applicable for String, Address, Name, or PhoneNumber data types'
            : undefined
        }
      />
      <Checkbox
        label="Track Changes In History"
        checked={fieldDefinition.TrackChangesInHistory}
        onChange={setField('TrackChangesInHistory')}
        disabled={readOnly}
      />
      <Checkbox
        label="Show In Public History"
        checked={fieldDefinition.ShowInPublicHistory}
        onChange={setField('ShowInPublicHistory')}
        disabled={readOnly}
      />
      <Checkbox
        label="Configurable as Filing Requirement"
        checked={fieldDefinition.ConfigurableAsFilingRequirement}
        onChange={setField('ConfigurableAsFilingRequirement')}
        disabled={!isFilingConfigurable || readOnly}
        description={
          !isFilingConfigurable
            ? 'Not applicable for Int, PhoneNumber, String, Address, or Name data types'
            : undefined
        }
      />
      <Checkbox
        label="Configurable as Filing Result"
        checked={fieldDefinition.ConfigurableAsFilingResult}
        onChange={setField('ConfigurableAsFilingResult')}
        disabled={!isFilingConfigurable || readOnly}
        description={
          !isFilingConfigurable
            ? 'Not applicable for Int, PhoneNumber, String, Address, or Name data types'
            : undefined
        }
      />

      <div className="field-definition-form__actions">
        {!readOnly && (
          <ContextButton onClick={onCancel} disabled={isSaving}>
            Cancel
          </ContextButton>
        )}
        {!readOnly && (
          <ContextButton variant="primary" onClick={handleSave} disabled={isSaving}>
            {fieldDefinition.MiscellaneousFieldId ? 'Save' : 'Add'}
          </ContextButton>
        )}
      </div>
    </div>
  )
}

export default FieldDefinitionForm
